import React, {useRef, useMemo} from 'react';
import {motion, useScroll, useTransform} from 'framer-motion';
import Countdown from 'react-countdown';
import MenuSelector from './MenuSelector';

const Hero = ({name, gender, weddingDate, effectiveDeadline, isVoted, menuToken, menuMember, menuFamily}) => {
    const greeting = gender === 'female' ? 'Дорогая' : 'Дорогой';
    const containerRef = useRef(null);

    // Проверяем, не прошел ли дедлайн
    const isDeadlinePassed = useMemo(() => {
        if (!effectiveDeadline) return false;
        const deadline = new Date(`${effectiveDeadline}T23:59:59+03:00`);
        return new Date() > deadline;
    }, [effectiveDeadline]);

    // Активная дата для таймера
    const activeDate = useMemo(() => {
        if (isDeadlinePassed) return weddingDate;
        return isVoted ? weddingDate : effectiveDeadline;
    }, [isDeadlinePassed, isVoted, weddingDate, effectiveDeadline]);

    const countdownTitle = (isDeadlinePassed || isVoted) ? "До свадьбы осталось" : "Подтвердить участие до";

    // Параллакс для всех устройств
    const {scrollYProgress} = useScroll({
        target: containerRef,
        offset: ["start start", "end start"]
    });

    const yLeft = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const yRight = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

    const formatWeddingDate = (dateStr) => {
        const date = new Date(dateStr);
        const options = {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'};
        const formatted = date.toLocaleDateString('ru-RU', options);
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const CountdownRenderer = ({days, hours, minutes, seconds, completed}) => {
        if (completed) {
            return (
                <div className="mt-8 lg:mt-12">
                    <p className="text-xs lg:text-sm uppercase tracking-[0.3em] mb-4 lg:mb-6 opacity-80 text-center">
                        Свадьба прошла
                    </p>
                    <div className="text-center">
                        <div
                            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-display text-gray-400">
                            ⌛
                        </div>
                        <div className="text-sm sm:text-base mt-2 text-gray-600">
                            Надеемся, вы хорошо провели время!
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="mt-8 lg:mt-12">
                <p className="text-xs lg:text-sm uppercase tracking-[0.3em] mb-4 lg:mb-6 opacity-80 text-center">
                    {countdownTitle}
                </p>
                <div className="flex justify-center space-x-2 sm:space-x-4 lg:space-x-8">
                    <div className="text-center">
                        <div
                            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-display">{days}</div>
                        <div className="text-xxs sm:text-xs uppercase tracking-[0.2em] mt-1 lg:mt-2 opacity-70">дней
                        </div>
                    </div>
                    <div className="text-center">
                        <div
                            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-display">{hours}</div>
                        <div className="text-xxs sm:text-xs uppercase tracking-[0.2em] mt-1 lg:mt-2 opacity-70">часов
                        </div>
                    </div>
                    <div className="text-center">
                        <div
                            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-display">{minutes}</div>
                        <div className="text-xxs sm:text-xs uppercase tracking-[0.2em] mt-1 lg:mt-2 opacity-70">минут
                        </div>
                    </div>
                    <div className="text-center">
                        <div
                            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-display">{seconds}</div>
                        <div className="text-xxs sm:text-xs uppercase tracking-[0.2em] mt-1 lg:mt-2 opacity-70">секунд
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const scrollToRSVP = () => {
        const rsvpSection = document.getElementById('rsvp-section');
        if (rsvpSection) {
            rsvpSection.scrollIntoView({behavior: 'smooth'});
        }
    };

    // Кнопку "Я приду" показываем только если дедлайн не прошёл и гость ещё не голосовал
    const showButton = !isDeadlinePassed && !isVoted;

    return (
        <section
            ref={containerRef}
            className="relative min-h-screen pt-12 sm:pt-16 md:pt-20 lg:pt-28 pb-12 sm:pb-16 md:pb-20"
        >
            {/* Десктоп версия: три колонки */}
            <div className="hidden lg:flex justify-between items-center px-8 lg:px-16">
                {/* Левое изображение */}
                <motion.div
                    className="relative w-1/5 h-[700px] overflow-hidden rounded-xl shadow-2xl"
                    initial={{opacity: 0, x: -30}}
                    animate={{opacity: 1, x: 0}}
                    transition={{duration: 1.2, delay: 0.2}}
                >
                    <div className="absolute inset-0 border-2 border-white/20 rounded-lg z-10"/>
                    <motion.div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: "url('/images/hero-left.jpg')",
                            backgroundPosition: '40% center',
                            y: yLeft
                        }}
                    />
                    <div
                        className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-transparent rounded-lg"/>
                </motion.div>

                {/* Центральная часть */}
                <motion.div
                    className="relative z-10 w-3/5 px-12"
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 1.2, delay: 0.4}}
                >
                    <div className="mb-12">
                        <p className="text-lg uppercase tracking-[0.5em] mb-4 text-gray-600 text-center">
                            Мы женимся
                        </p>
                        <div className="flex items-center justify-center mb-8">
                            <motion.div
                                className="text-6xl md:text-7xl lg:text-8xl font-display font-light"
                                initial={{opacity: 0, x: -30}}
                                animate={{opacity: 1, x: 0}}
                                transition={{delay: 0.6}}
                            >
                                Татьяна
                            </motion.div>
                            <motion.div
                                className="mx-4 text-4xl md:text-5xl font-display text-gray-400"
                                initial={{scale: 0}}
                                animate={{scale: 1}}
                                transition={{delay: 0.8}}
                            >
                                &
                            </motion.div>
                            <motion.div
                                className="text-6xl md:text-7xl lg:text-8xl font-display font-light"
                                initial={{opacity: 0, x: 30}}
                                animate={{opacity: 1, x: 0}}
                                transition={{delay: 0.6}}
                            >
                                Илья
                            </motion.div>
                        </div>
                        <motion.div
                            className="h-px w-32 bg-gray-300 mb-8 mx-auto"
                            initial={{width: 0}}
                            animate={{width: 128}}
                            transition={{delay: 1, duration: 1}}
                        />
                        <motion.div
                            className="text-2xl md:text-3xl font-display mb-2 text-gray-700 text-center"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{delay: 1.2}}
                        >
                            {formatWeddingDate(weddingDate)}
                        </motion.div>
                        <motion.div
                            className="text-lg uppercase tracking-[0.3em] text-gray-500 text-center"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{delay: 1.4}}
                        >
                            Санкт-Петербург
                        </motion.div>
                    </div>

                    <motion.div
                        className="mb-12"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{delay: 1.6}}
                    >
                        <p className="text-xl md:text-2xl mb-2 text-center">
                            {greeting} <span className="font-semibold">{name}</span>
                        </p>
                        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed text-center">
                            Мы рады пригласить вас на наше торжество и разделить с вами этот особенный день
                        </p>
                    </motion.div>

                    {showButton && (
                        <motion.button
                            onClick={scrollToRSVP}
                            className="mt-16 px-12 py-4 bg-gray-900 text-white text-lg uppercase tracking-[0.3em] border-2 border-black hover:bg-gray-800 transition-all duration-300 group relative overflow-x-hidden mx-auto block"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{delay: 2}}
                            whileHover={{scale: 1.05}}
                            whileTap={{scale: 0.95}}
                        >
                            <span className="relative z-10 group-hover:text-black">Я приду</span>
                            <span
                                className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"/>
                        </motion.button>
                    )}

                    {/* Таймер – показываем всегда (с активной датой и заголовком) */}
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{delay: 1.8}}
                    >
                        <Countdown
                            date={new Date(`${activeDate}T23:59:59+03:00`)}
                            renderer={CountdownRenderer}
                        />
                    </motion.div>

                    <MenuSelector token={menuToken} member={menuMember} family={menuFamily}/>
                </motion.div>

                {/* Правое изображение */}
                <motion.div
                    className="hidden lg:block relative w-1/5 h-[700px] overflow-hidden rounded-xl shadow-2xl"
                    initial={{opacity: 0, x: 30}}
                    animate={{opacity: 1, x: 0}}
                    transition={{duration: 1.2, delay: 0.2}}
                >
                    <div className="absolute inset-0 border-2 border-white/20 rounded-lg z-10"/>
                    <motion.div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: "url('/images/hero-right.jpg')",
                            backgroundPosition: 'center',
                            y: yRight,
                            top: '-5%'
                        }}
                    />
                    <div
                        className="absolute inset-0 bg-gradient-to-l from-black/10 via-transparent to-transparent rounded-lg"/>
                </motion.div>
            </div>

            {/* Мобильная версия */}
            <div className="lg:hidden px-4 sm:px-6">
                <motion.div
                    className="relative z-10"
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 1.2, delay: 0.4}}
                >
                    <div className="mb-8">
                        <p className="text-sm sm:text-lg uppercase tracking-[0.3em] sm:tracking-[0.5em] mb-3 sm:mb-4 text-gray-600 text-center">
                            Мы женимся
                        </p>
                        <div className="flex items-center justify-center mb-6 space-x-2 sm:space-x-4">
                            <motion.div
                                className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-display font-light whitespace-nowrap"
                                initial={{opacity: 0, x: -30}}
                                animate={{opacity: 1, x: 0}}
                                transition={{delay: 0.6}}
                            >
                                Татьяна
                            </motion.div>
                            <motion.div
                                className="text-2xl sm:text-3xl md:text-4xl font-display text-gray-400"
                                initial={{scale: 0}}
                                animate={{scale: 1}}
                                transition={{delay: 0.8}}
                            >
                                &
                            </motion.div>
                            <motion.div
                                className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-display font-light whitespace-nowrap"
                                initial={{opacity: 0, x: 30}}
                                animate={{opacity: 1, x: 0}}
                                transition={{delay: 0.6}}
                            >
                                Илья
                            </motion.div>
                        </div>
                        <motion.div
                            className="h-px w-24 xs:w-32 bg-gray-300 mb-6 sm:mb-8 mx-auto"
                            initial={{width: 0}}
                            animate={{width: '100%'}}
                            transition={{delay: 1, duration: 1}}
                        />
                        <motion.div
                            className="text-xl sm:text-2xl md:text-3xl font-display mb-2 text-gray-700 text-center"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{delay: 1.2}}
                        >
                            {formatWeddingDate(weddingDate)}
                        </motion.div>
                        <motion.div
                            className="text-sm sm:text-lg uppercase tracking-[0.2em] sm:tracking-[0.3em] text-gray-500 text-center"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{delay: 1.4}}
                        >
                            Санкт-Петербург
                        </motion.div>
                    </div>

                    <motion.div
                        className="mb-8"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{delay: 1.6}}
                    >
                        <p className="text-lg sm:text-xl md:text-2xl mb-2 text-center">
                            {greeting} <span className="font-semibold">{name}</span>
                        </p>
                        <p className="text-base sm:text-lg text-gray-600 mx-auto leading-relaxed text-center max-w-md">
                            Мы рады пригласить вас на наше торжество и разделить с вами этот особенный день
                        </p>
                    </motion.div>

                    {showButton && (
                        <motion.button
                            onClick={scrollToRSVP}
                            className="mt-10 sm:mt-16 px-8 sm:px-12 py-3 sm:py-4 bg-gray-900 text-white text-base sm:text-lg uppercase tracking-[0.2em] sm:tracking-[0.3em] border-2 border-black hover:bg-gray-800 transition-all duration-300 group relative overflow-x-hidden mx-auto block"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{delay: 2}}
                            whileHover={{scale: 1.05}}
                            whileTap={{scale: 0.95}}
                        >
                            <span className="relative z-10 group-hover:text-black">Я приду</span>
                            <span
                                className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"/>
                        </motion.button>
                    )}

                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{delay: 1.8}}
                    >
                        <Countdown
                            date={new Date(`${activeDate}T23:59:59+03:00`)}
                            renderer={CountdownRenderer}
                        />
                    </motion.div>

                    <MenuSelector token={menuToken} member={menuMember} family={menuFamily}/>
                </motion.div>

                <div className="flex justify-between items-center mt-8 sm:mt-12 space-x-4">
                    <motion.div
                        className="relative w-5/12 h-[300px] xs:h-[350px] sm:h-[400px] overflow-hidden rounded-xl shadow-xl"
                        initial={{opacity: 0, x: -20}}
                        animate={{opacity: 1, x: 0}}
                        transition={{duration: 1.2, delay: 0.2}}
                    >
                        <div className="absolute inset-0 border-2 border-white/20 rounded-lg z-10"/>
                        <motion.div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{
                                backgroundImage: "url('/images/hero-left.jpg')",
                                backgroundPosition: '40% center',
                                y: yLeft
                            }}
                        />
                        <div
                            className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-transparent rounded-lg"/>
                    </motion.div>
                    <motion.div
                        className="relative w-5/12 h-[300px] xs:h-[350px] sm:h-[400px] overflow-hidden rounded-xl shadow-xl"
                        initial={{opacity: 0, x: 20}}
                        animate={{opacity: 1, x: 0}}
                        transition={{duration: 1.2, delay: 0.2}}
                    >
                        <div className="absolute inset-0 border-2 border-white/20 rounded-lg z-10"/>
                        <motion.div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{
                                backgroundImage: "url('/images/hero-right.jpg')",
                                backgroundPosition: 'center',
                                y: yRight,
                                top: '-5%'
                            }}
                        />
                        <div
                            className="absolute inset-0 bg-gradient-to-l from-black/10 via-transparent to-transparent rounded-lg"/>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;